// lib/AuthContext.tsx — Supabase Auth
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { supabase } from './supabaseClient';
import type { User } from '@supabase/supabase-js';

/** Mimics Firebase User shape for minimal UI changes: id, email, emailVerified, metadata, displayName */
export interface AuthUserLike {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName?: string | null;
  photoURL?: string | null;
  metadata?: { creationTime?: string };
}

function mapSupabaseUser(user: User | null): AuthUserLike | null {
  if (!user) return null;
  return {
    uid: user.id,
    email: user.email ?? null,
    emailVerified: !!user.email_confirmed_at,
    displayName: user.user_metadata?.full_name ?? user.email?.split('@')[0] ?? null,
    photoURL: user.user_metadata?.avatar_url ?? user.user_metadata?.picture ?? null,
    metadata: user.created_at ? { creationTime: new Date(user.created_at).toISOString() } : undefined,
  };
}

interface AuthContextType {
  user: AuthUserLike | null;
  loading: boolean;
  /** Raw Supabase user for any code that needs it */
  supabaseUser: User | null;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  supabaseUser: null,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    const isPageSpeedInsights =
      navigator.userAgent.includes('Chrome-Lighthouse') ||
      navigator.userAgent.includes('PageSpeed') ||
      navigator.userAgent.includes('GTmetrix') ||
      (window.navigator as any).webdriver === true;

    if (isPageSpeedInsights) {
      setLoading(false);
      return;
    }

    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSupabaseUser(session?.user ?? null);
      } catch {
        setSupabaseUser(null);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSupabaseUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const user = mapSupabaseUser(supabaseUser);

  return (
    <AuthContext.Provider value={{ user, loading, supabaseUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
